import { DatePipe, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXsrfConfiguration } from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID, importProvidersFrom, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TitleStrategy, provideRouter } from '@angular/router';

// Custom
import { AppInitializerService, AuthInterceptor, AuthModuleFactory, SimacTheme } from '@simac/ui';
import { ConfirmationService, MessageService } from '@openng/optimus-ui/api';
import { environment } from 'src/environments/environment';
import { TemplatePageTitleStrategy, routes } from './app.routes';

// 3rd party
import { provideMarkdown } from 'ngx-markdown';
import { TranslateService, provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthModule, StsConfigLoader } from 'angular-auth-oidc-client';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideOptimus } from '@openng/optimus-ui/config';

import localeNl from '@angular/common/locales/nl';
import { provideServiceWorker } from '@angular/service-worker';
import { PrintService } from '@shared/print/services/print.service';
registerLocaleData(localeNl);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(() => {
      const initializerService: AppInitializerService = inject(AppInitializerService);
      return initializerService.loadAppInfo(environment.version);
    }),
    provideZonelessChangeDetection(),
    provideHttpClient(withXsrfConfiguration({ cookieName: 'MySimac.XSRF-TOKEN', headerName: 'X-MySimac-Antiforgery' }), withInterceptors([AuthInterceptor])),
    provideRouter(routes),
    provideOptimus({
      theme: {
        preset: SimacTheme,
        options: {
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'optimus-ui',
            order: 'theme, base, optimus',
          },
        },
      },
    }),
    provideMarkdown(),
    provideServiceWorker(`ngsw-worker.js?v=${environment.version}`, {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:3000',
    }),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        resources: [
          './assets/ui/i18n/',
          { prefix: './assets/i18n/shared/', suffix: '.json' },
          { prefix: './assets/i18n/serviceandsupport/', suffix: '.json' },
          { prefix: './assets/i18n/itsm/', suffix: '.json' },
        ],
      }),
    }),
    importProvidersFrom([
      NgxPermissionsModule.forRoot(),
      MonacoEditorModule.forRoot(),
      AuthModule.forRoot({
        loader: {
          provide: StsConfigLoader,
          useFactory: AuthModuleFactory,
          deps: [AppInitializerService],
        },
      }),
    ]),
    {
      provide: TitleStrategy,
      useClass: TemplatePageTitleStrategy,
      deps: [AppInitializerService, Title, TranslateService],
    },
    {
      provide: LOCALE_ID,
      useValue: 'nl-NL',
    },
    PrintService,
    MessageService,
    ConfirmationService,
    DatePipe,
  ],
};
